import { useMemo } from 'react';
import { DeviceType } from '@weave/schema-gen-ts/dist/schemas/phone/user/v1/user_service.pb';
import { locationApiQueryKeys } from '@frontend/api-location';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { attachLocationTypes, WeaveLocation, WeaveLocationGroup } from '@frontend/scope';
import { extractParentLocations, transformLocationData, queryOrgLocationsWithTenantIds } from './utils';

export const useWeaveQueryLocations = ({ orgId, enabled = true }: { orgId: string; enabled?: boolean }) => {
  const queryKey = locationApiQueryKeys.orgLocations(orgId);
  const { data: orgLocations, isLoading } = useQuery({
    queryKey,
    queryFn: () => queryOrgLocationsWithTenantIds(orgId),
    enabled: !!orgId && enabled,
  });

  const selectOrgLocations = useMemo(() => extractParentLocations(orgLocations), [orgLocations]);

  const accessibleLocationData = useMemo(
    () => attachLocationTypes(transformLocationData(orgLocations)),
    [orgLocations]
  );

  return { orgLocations, isLoading, selectOrgLocations, accessibleLocationData };
};

export const usePhoneConfigInfo = ({
  phoneConfig,
  accessibleLocationData,
}: {
  phoneConfig: ReturnType<typeof usePhoneConfigShallowStore>['phoneConfig'];
  accessibleLocationData: Record<string, WeaveLocation | WeaveLocationGroup>;
}) => {
  return useMemo(() => {
    if (!phoneConfig?.locationId) return;
    const location = accessibleLocationData[phoneConfig.locationId];
    if (!location) return;

    const isSoftphone = phoneConfig.deviceType === DeviceType.DEVICE_TYPE_SOFTPHONE;

    return {
      orgId: location.orgId,
      locationId: location.id,
      isSoftphone,
    };
  }, [phoneConfig, accessibleLocationData]);
};
